import React from 'react';
import { useState } from 'react';
import { ICampaign, IDevice, IUser } from '../../interfaces';
import Calendar from 'react-calendar';
import { Targeting } from './Targeting';
import { useEffect } from 'react';
import {
    deleteCampaign,
    getFilteredDevices,
    getShopNb,
    getSuggestions,
    saveNewCampaign,
    signCampaign,
    updateCampaign
} from '../../utils/pricing';
import { PlanningBlock, PlanningWeek } from '../PlanningBlock';
import { Planning } from './Planning';
import { getCampaignsOverlapping } from '../../utils/campaign-viewer';
import { flattenCampaigns, generateCampaignObject, groupCampaigns } from '../../utils';
import { Pricing } from './Pricing';
import Swal from 'sweetalert2';
import { MapView } from './MapView';
import { PoiManager } from './PoiManager';
import { ModalManualSelection } from './ModalManualSelection';
import { DrawMap } from './DrawMap';
import { ModalFileUpload } from './ModalFileUpload';

interface EditorStateCampaign extends ICampaign {
    isNew: boolean;
}

interface IState {
    campaign: EditorStateCampaign;
    filtersSign: any[];
    filtersOther: any[];
    suggestions: any[];
    signSuggestions: any[];
    devices: { [key: number]: IDevice };
    exclusion: boolean;
    mapModalOpened: boolean;
    poiModalOpened: boolean;
    poiDrawOpened: boolean;
    uploadModalOpened: boolean;
    manualSelectionModalOpened: boolean;
    brandResults: { domain: string; logo: string; name: string }[];
}

export const NewCampaign = (props: {
    availableDevices: IDevice[];
    campaigns: ICampaign[];
    baseCampaign: ICampaign | null;
    onAddCampaign: (camp: ICampaign) => void;
    onDeleteCampaign: (camp: ICampaign) => void;
    onUpdateCampaign: (camp: ICampaign) => void;
    onUpdateCampaignLocal: (camp: ICampaign) => void;
    user: IUser;
}) => {
    const weekBegin = new Date(new Date().setHours(0, 0, 0, 0));
    weekBegin.setDate(weekBegin.getDate() - weekBegin.getDay() + 1);

    const weekEnd = new Date(weekBegin);
    weekEnd.setDate(weekEnd.getDate() + 7);
    weekEnd.setTime(weekEnd.getTime() - 1);

    const [state, setState] = useState<IState>({
        campaign: {
            name: 'Nouvelle campagne',
            originalName: 'Nouvelle campagne',
            active: false,
            advertiser: '',
            brand: '',
            creatives: [],
            devices: [],
            endDate: weekEnd,
            startDate: weekBegin,
            groupedIds: [],
            id: '0',
            planning: new Array(7).fill(null).map(() => new Array(16).fill(null).map((_, i) => i + 7)),
            signs: {},
            slots: 1,
            state: 'draft',
            groupName: 'Nouvelle campagne',
            isNew: true,
            hideFromInterface: true,
            revenue: 0
        },
        filtersSign: [],
        filtersOther: [],
        signSuggestions: [],
        suggestions: [],
        devices: {},
        exclusion: false,
        mapModalOpened: false,
        poiModalOpened: false,
        poiDrawOpened: false,
        uploadModalOpened: false,
        manualSelectionModalOpened: false,
        brandResults: []
    });

    useEffect(() => {
        const { suggestions, signSuggestions } = getSuggestions(props.availableDevices);

        const devices: { [key: number]: IDevice } = {};
        for (const d of props.availableDevices) {
            devices[d.id] = d;
        }

        window.scrollTo({ top: 0, behavior: 'auto' });

        setState((prevState) => ({
            ...prevState,
            suggestions,
            signSuggestions,
            devices,
            campaign: props.baseCampaign
                ? {
                      ...prevState.campaign,
                      ...props.baseCampaign,
                      advertiser: '',
                      isNew: false
                  }
                : { ...prevState.campaign }
        }));
    }, [props.availableDevices, props.baseCampaign]);

    const groupedCampaigns = flattenCampaigns(groupCampaigns(props.campaigns), true);

    const handleSelectDevices = (
        filter: any,
        operation: 'add' | 'delete',
        filterName: 'filtersSign' | 'filtersOther'
    ) => {
        if (operation === 'add') {
            const filters = {
                filtersSign: state.filtersSign,
                filtersOther: state.filtersOther
            };

            filters[filterName].push(filter);

            setState((prevState) => ({
                ...prevState,
                [filterName]: filters[filterName],
                campaign: {
                    ...prevState.campaign,
                    devices: getFilteredDevices(
                        props.availableDevices,
                        [...filters.filtersOther, ...filters.filtersSign],
                        false,
                        state.exclusion
                    )
                }
            }));
        } else if (operation === 'delete') {
            const filters = {
                filtersSign: state.filtersSign,
                filtersOther: state.filtersOther,
                [filterName]: state[filterName].filter((_, i) => i !== filter)
            };

            setState((prevState) => ({
                ...prevState,
                [filterName]: filters[filterName],
                campaign: {
                    ...prevState.campaign,
                    devices: getFilteredDevices(
                        props.availableDevices,
                        [...filters.filtersOther, ...filters.filtersSign],
                        false,
                        state.exclusion
                    )
                }
            }));
        }
    };

    return (
        <div className="newCampaign">
            <div className="row">
                <div className="panel">
                    <div className="panel-heading">
                        <h4>Projet</h4>
                    </div>
                    <div className="panel-body flex-col">
                        <div className="card mb-10">
                            <div className="form-group">
                                <label>Nom du projet</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={state.campaign.originalName}
                                    onChange={(e) => {
                                        setState((prevState) => ({
                                            ...prevState,
                                            campaign: {
                                                ...prevState.campaign,
                                                originalName: e.target.value,
                                                name: e.target.value
                                            }
                                        }));
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Nom court (nom planning)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={state.campaign.groupName}
                                    onChange={(e) => {
                                        setState((prevState) => ({
                                            ...prevState,
                                            campaign: {
                                                ...prevState.campaign,
                                                groupName: e.target.value
                                            }
                                        }));
                                    }}
                                />
                            </div>
                        </div>
                        <div className="card mb-10">
                            <Calendar
                                showWeekNumbers={true}
                                minDetail="month"
                                next2Label={null}
                                prev2Label={null}
                                onClickWeekNumber={(_: any, date: Date) => null}
                                selectRange={true}
                                tileClassName={({ date, view }: { date: Date; view: string }) => {
                                    if (view !== 'month') {
                                        return null;
                                    }

                                    if (date >= state.campaign.startDate && date < state.campaign.endDate) {
                                        return 'selected';
                                    }

                                    return null;
                                }}
                                onChange={(e) => {
                                    const data = e as any as Date[];
                                    setState((prevState) => ({
                                        ...prevState,
                                        campaign: {
                                            ...prevState.campaign,
                                            endDate: data[1],
                                            startDate: data[0]
                                        }
                                    }));
                                }}
                            />
                        </div>
                        <div className="brand">
                            <div className="form-group">
                                <label>Marque</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    onChange={(e) => {
                                        if (e.target.value.length < 2) return;

                                        fetch(
                                            `https://autocomplete.clearbit.com/v1/companies/suggest?query=${e.target.value}`
                                        )
                                            .then((res) => res.json())
                                            .then((json) =>
                                                setState((prevState) => ({ ...prevState, brandResults: json }))
                                            );
                                    }}
                                />
                            </div>
                            <p>Resultats:</p>
                            <div className="logos">
                                {state.brandResults.map((r) => (
                                    <div key={r.domain}>
                                        <img
                                            src={r.logo}
                                            alt={r.name}
                                            onClick={() =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    campaign: {
                                                        ...prevState.campaign,
                                                        logoUrl: r.logo
                                                    }
                                                }))
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                            {!state.campaign.logoUrl || (
                                <React.Fragment>
                                    <p>Actuel:</p>
                                    <img src={state.campaign.logoUrl} alt="logo" />
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>
                <div className="panel">
                    <div className="panel-heading">
                        <h4>Sélection écrans</h4>
                    </div>
                    <div className="panel-body">
                        <div className="card">
                            <div className="selection">
                                <Targeting
                                    availableDevices={props.availableDevices}
                                    filtersOther={state.filtersOther}
                                    filtersSign={state.filtersSign}
                                    signSuggestions={state.signSuggestions}
                                    suggestions={state.suggestions}
                                    selected={state.campaign.devices}
                                    handleSelectDevices={handleSelectDevices}
                                    exclusion={state.exclusion}
                                    onChangeExclude={(exclusion) =>
                                        setState((prevState) => ({
                                            ...prevState,
                                            exclusion,
                                            campaign: {
                                                ...prevState.campaign,
                                                devices: getFilteredDevices(
                                                    props.availableDevices,
                                                    [...state.filtersOther, ...state.filtersSign],
                                                    false,
                                                    exclusion
                                                )
                                            }
                                        }))
                                    }
                                    onSelectAll={(all) =>
                                        setState((prevState) => ({
                                            ...prevState,
                                            campaign: {
                                                ...prevState.campaign,
                                                devices: all ? [...props.availableDevices.map(({ id }) => id)] : []
                                            }
                                        }))
                                    }
                                    openMap={() => setState({ ...state, mapModalOpened: true })}
                                    openPois={() => setState({ ...state, poiModalOpened: true })}
                                    openDraw={() => setState({ ...state, poiDrawOpened: true })}
                                    openUpload={() => setState({ ...state, uploadModalOpened: true })}
                                    openManualSelection={() => setState({ ...state, manualSelectionModalOpened: true })}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="panel slots">
                    <div className="panel-heading">
                        <h4>Nombre de répétitions par minutes</h4>
                    </div>
                    <div className="card">
                        <div className="slots">
                            <div className="form-group">
                                <label htmlFor="slots">Nombre de r&eacute;p&eacute;titions par minutes (*)</label>
                                <input
                                    type="number"
                                    name="slots"
                                    className="form-control"
                                    value={state.campaign.slots}
                                    onChange={(e) => {
                                        if (
                                            isNaN(parseInt(e.target.value)) ||
                                            parseInt(e.target.value) < 1 ||
                                            parseInt(e.target.value) > 5
                                        ) {
                                            return;
                                        }

                                        setState((prevState) => ({
                                            ...prevState,
                                            campaign: {
                                                ...prevState.campaign,
                                                slots: parseInt(e.target.value)
                                            }
                                        }));
                                    }}
                                />
                            </div>
                            <p className="mb-0 justify">
                                <span className="price">
                                    * La boucle DOOHYOULIKE dure 60 secondes et, est composée de maximum 5 créneaux
                                    publicitaires de 10 secondes.
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="panel">
                    <div className="panel-heading">
                        <h4>Horaires</h4>
                    </div>
                    <div className="panel-body">
                        <div className="card">
                            <div className="slots-selection">
                                <PlanningBlock
                                    planning={[...state.campaign.planning.map((p) => [...p])]}
                                    setPlanning={(planning) =>
                                        setState({
                                            ...state,
                                            campaign: {
                                                ...state.campaign,
                                                planning
                                            }
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="panel">
                    <div className="panel-heading">
                        <h4>Visualisation du planning</h4>
                    </div>
                    <div className="panel-body">
                        <div className="card">
                            <PlanningWeek
                                planning={[...state.campaign.planning]}
                                setPlanning={(planning) =>
                                    setState({
                                        ...state,
                                        campaign: {
                                            ...state.campaign,
                                            planning
                                        }
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="panel large">
                    <div className="planning-ctr">
                        <Planning
                            campaigns={getCampaignsOverlapping(state.campaign, groupedCampaigns)}
                            updateCampaign={props.onUpdateCampaignLocal}
                            devices={state.devices}
                            setPlanning={(planning) =>
                                setState((prevState) => ({
                                    ...prevState,
                                    campaign: {
                                        ...state.campaign,
                                        planning
                                    }
                                }))
                            }
                            onRemoveDevices={(devices) =>
                                setState((prevState) => ({
                                    ...prevState,
                                    campaign: {
                                        ...prevState.campaign,
                                        devices: prevState.campaign.devices.filter((i) => !devices.includes(i))
                                    }
                                }))
                            }
                            user={props.user}
                        />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="panel large">
                    <div className="panel-heading">
                        <h4>Pricing par semaine</h4>
                    </div>
                    <div className="panel-body">
                        <Pricing
                            hideFromInterface={state.campaign.hideFromInterface}
                            startDate={state.campaign.startDate}
                            endDate={state.campaign.endDate}
                            planning={state.campaign.planning}
                            screenNumber={state.campaign.devices.length}
                            shopsNumber={getShopNb(
                                state.campaign.devices.map((id) => state.devices[id]).filter((d) => !!d)
                            )}
                            slots={state.campaign.slots}
                        />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="panel">
                    <div className="panel-heading">
                        <h4>Actions</h4>
                    </div>
                    <div className="panel-body">
                        <button
                            className="btn"
                            onClick={() => {
                                if (state.campaign.isNew) {
                                    saveNewCampaign(state.campaign)
                                        .then((id) => {
                                            Swal.fire({ text: 'Campagne créée', icon: 'success' });
                                            props.onAddCampaign({ ...state.campaign, id });
                                        })
                                        .catch(() =>
                                            Swal.fire({
                                                text: 'Une erreur est survenue lors de la création de la campagne',
                                                icon: 'error'
                                            })
                                        );
                                } else {
                                    updateCampaign(state.campaign)
                                        .then(() => {
                                            Swal.fire({ text: 'Campagne mise à jour', icon: 'success' });
                                            props.onUpdateCampaign(state.campaign);
                                        })
                                        .catch(() =>
                                            Swal.fire({
                                                text: 'Une erreur est survenue lors de la mise à jour de la campagne',
                                                icon: 'error'
                                            })
                                        );
                                }
                            }}
                        >
                            <span className="material-icons">save</span> Enregistrer le brouillon
                        </button>
                        <button
                            className="btn"
                            disabled={state.campaign.isNew}
                            onClick={() => {
                                Swal.fire({
                                    text: 'Ce projet de campagne sera marqué comme signé et ne sera plus modifiable. Etes-vous surs?',
                                    icon: 'question',
                                    showConfirmButton: true,
                                    confirmButtonText: 'Oui',
                                    cancelButtonText: 'Annuler',
                                    showCancelButton: true
                                }).then((r) => {
                                    if (r.isConfirmed) {
                                        signCampaign(state.campaign)
                                            .then((campaign) => {
                                                Swal.fire({ text: 'Campagne signée', icon: 'success' });
                                                props.onUpdateCampaign(
                                                    generateCampaignObject(campaign, props.availableDevices)
                                                );
                                            })
                                            .catch(() =>
                                                Swal.fire({
                                                    text: 'Une erreur est survenue lors de la mise à jour de la campagne',
                                                    icon: 'error'
                                                })
                                            );
                                    }
                                });
                            }}
                        >
                            Marquer comme signé
                        </button>
                        <button
                            className="btn danger"
                            onClick={() =>
                                Swal.fire({
                                    text: 'Ce projet de campagne sera supprimé définitivement. Etes-vous surs?',
                                    icon: 'question',
                                    showConfirmButton: true,
                                    confirmButtonText: 'Oui',
                                    cancelButtonText: 'Annuler',
                                    showCancelButton: true
                                }).then((r) => {
                                    if (r.isConfirmed) {
                                        deleteCampaign(state.campaign)
                                            .then(() => {
                                                Swal.fire({ text: 'Campagne supprimée', icon: 'success' });
                                                props.onDeleteCampaign(state.campaign);
                                            })
                                            .catch(() =>
                                                Swal.fire({
                                                    text: 'Une erreur est survenue lors de la suppression de la campagne',
                                                    icon: 'error'
                                                })
                                            );
                                    }
                                })
                            }
                            disabled={state.campaign.isNew}
                        >
                            Supprimer ce projet
                        </button>
                    </div>
                </div>
            </div>
            {!state.mapModalOpened || (
                <MapView
                    close={() => setState({ ...state, mapModalOpened: false })}
                    devices={state.campaign.devices.map((id) => state.devices[id])}
                />
            )}
            {!state.poiDrawOpened || (
                <DrawMap
                    close={() => setState({ ...state, poiDrawOpened: false })}
                    onAddDevices={(devices_to_add) => {
                        const devices = [...state.campaign.devices];

                        for (const d of devices_to_add) {
                            if (devices.indexOf(d.id) === -1) {
                                devices.push(d.id);
                            }
                        }

                        setState((prevState) => ({
                            ...prevState,
                            campaign: {
                                ...prevState.campaign,
                                devices
                            },
                            poiDrawOpened: false
                        }));
                    }}
                    allDevices={props.availableDevices}
                />
            )}
            {!state.uploadModalOpened || (
                <ModalFileUpload
                    close={() => setState({ ...state, uploadModalOpened: false })}
                    selectedDevices={state.campaign.devices}
                    availableDevices={props.availableDevices}
                    onSelectList={(list) =>
                        setState((prevState) => ({
                            ...prevState,
                            campaign: {
                                ...prevState.campaign,
                                devices: list
                            },
                            uploadModalOpened: false
                        }))
                    }
                />
            )}
            {!state.poiModalOpened || (
                <PoiManager
                    onAddDevices={(devices_to_add) => {
                        const devices = [...state.campaign.devices];

                        for (const d of devices_to_add) {
                            if (devices.indexOf(d.id) === -1) {
                                devices.push(d.id);
                            }
                        }

                        setState((prevState) => ({
                            ...prevState,
                            campaign: {
                                ...prevState.campaign,
                                devices
                            },
                            poiModalOpened: false
                        }));
                    }}
                    allDevices={props.availableDevices}
                    close={() => setState({ ...state, poiModalOpened: false })}
                />
            )}
            {!state.manualSelectionModalOpened || (
                <ModalManualSelection
                    availableDevices={props.availableDevices}
                    selectedDevices={new Set(state.campaign.devices)}
                    onAdd={(deviceId) =>
                        setState((prevState) => ({
                            ...prevState,
                            campaign: {
                                ...prevState.campaign,
                                devices: [...prevState.campaign.devices, deviceId]
                            }
                        }))
                    }
                    onRemove={(deviceId) =>
                        setState((prevState) => ({
                            ...prevState,
                            campaign: {
                                ...prevState.campaign,
                                devices: prevState.campaign.devices.filter((i) => i !== deviceId)
                            }
                        }))
                    }
                    close={() => setState({ ...state, manualSelectionModalOpened: false })}
                />
            )}
        </div>
    );
};
